import { AncillarySelect } from "@/components/data-driven-inputs/selects/ancillary-select/AncillarySelect";
import { NumericTextInput } from "@/components/numeric-text-input/NumericTextInput";
import { FormModelChangeHandler } from "@/types/forms/types";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import { PriceCalculatorFormData } from "@/features/price-calculator/types/types";
import { Box, Row, Space } from "@stenajs-webui/core";
import {
  FlatButton,
  SecondaryButton,
  stenaMinus,
  stenaPlusCircle,
} from "@stenajs-webui/elements";
import { FieldCaption } from "@/components/forms/FieldCaption";
import { GapValues } from "@/lib/utils/GapValues";
import { ConditionalTooltip } from "@/components/ConditionalTooltip";
import { replaceAtIndex } from "@/lib/list/ReplaceAtIndex";

interface Props {
  formData: PriceCalculatorFormData;
  onChangeFormData: FormModelChangeHandler<PriceCalculatorFormData>;
}

export const PriceCalculatorAddAncillaryRow: React.FC<Props> = ({
  formData,
  onChangeFormData,
}) => {
  const isAncillaryQuantityEmpty = (index: number) =>
    !formData?.conditionsAncillary?.[index]?.quantity;
  return (
    <Row gap={GapValues.TWENTYFOUR}>
      <Box width={200}>
        <FieldCaption label={"Ancillary code"}>
          {formData?.conditionsAncillary?.map((ancillary, index) => (
            <Box key={`ancillary-select-${index}`}>
              <AncillarySelect
                value={ancillary.ancillaryCode}
                onValueChange={(ancillaryCode) =>
                  onChangeFormData(
                    "conditionsAncillary",
                    replaceAtIndex(formData.conditionsAncillary ?? [], index, {
                      ancillaryCode,
                    })
                  )
                }
                height={"38px"}
                menuPortalTarget={document.body}
                menuPlacement={"auto"}
                minMenuHeight={300}
              />
              <Space num={SpaceValues.SIXTEEN} />
            </Box>
          ))}
          <SecondaryButton
            label={"Add ancillary"}
            leftIcon={stenaPlusCircle}
            onClick={() => {
              onChangeFormData("conditionsAncillary", [
                ...(formData?.conditionsAncillary || []),
                { ancillaryCode: "", quantity: 1 },
              ]);
            }}
            size={"medium"}
          />
        </FieldCaption>
      </Box>
      <FieldCaption label={"Quantity"}>
        {formData.conditionsAncillary?.length === 0 ? (
          <Row gap={GapValues.TWENTYFOUR}>
            <NumericTextInput disabled />
            <FlatButton leftIcon={stenaMinus} />
          </Row>
        ) : (
          formData?.conditionsAncillary?.map((ancillary, index) => (
            <Row gap={GapValues.TWENTYFOUR} key={`quantity-row-${index}`}>
              <Box width={187}>
                <ConditionalTooltip
                  label={
                    isAncillaryQuantityEmpty(index)
                      ? "Please provide a quantity above 0"
                      : ""
                  }
                  zIndex={ZIndex.lowest}
                  variant="warning"
                  appendTo={document.body}
                  shouldShow={isAncillaryQuantityEmpty(index)}
                >
                  <NumericTextInput
                    value={
                      isAncillaryQuantityEmpty(index)
                        ? ""
                        : ancillary.quantity.toString()
                    }
                    onValueChange={(quantity) =>
                      onChangeFormData(
                        "conditionsAncillary",
                        replaceAtIndex(
                          formData.conditionsAncillary ?? [],
                          index,
                          { quantity: parseInt(quantity) }
                        )
                      )
                    }
                    variant={
                      isAncillaryQuantityEmpty(index) ? "warning" : "standard"
                    }
                  />
                </ConditionalTooltip>
                <Space num={SpaceValues.SIXTEEN} />
              </Box>
              <FlatButton
                leftIcon={stenaMinus}
                onClick={() =>
                  onChangeFormData("conditionsAncillary", [
                    ...(formData?.conditionsAncillary || []).filter(
                      (item, formIndex) => index !== formIndex
                    ),
                  ])
                }
              />
            </Row>
          ))
        )}
      </FieldCaption>
    </Row>
  );
};
